/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {calculateNetPrice} from './utils'
import {CUSTOMIZABLE_SET_DISPLAY_TEMPLATE} from '../constants'

/**
 * Get the human-friendly version of the variation values that users have selected.
 * Useful for displaying these values in the UI.
 *
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {Object} values - The variations selected attribute values.
 * @returns {Object} - A key value map of the display name and display value.
 *
 * @example
 * const displayValues = getDisplayVariationValues(
 *     [ { "id": "color", "name": "Colour", "values": [ { "name": "royal", "orderable": true, "value": "JJ5FUXX" } ] } ],
 *     { "color": "JJ5FUXX" }
 * )
 * // returns { "Colour": "royal" }
 */
export const getDisplayVariationValues = (variationAttributes, values = {}) => {
    const returnVal = Object.entries(values).reduce((acc, [id, value]) => {
        const attribute = variationAttributes.find(({id: attributeId}) => attributeId === id)
        const attributeValue = attribute.values.find(
            ({value: attributeValue}) => attributeValue === value
        )
        return {
            ...acc,
            [attribute.name]: attributeValue.name
        }
    }, {})
    return returnVal
}

/**
 * Normalizes data for product sets and product bundles into the same format
 * Useful for operations that apply to both product sets and product bundles
 *
 * @param {Object} product - A product set or product bundle
 * @returns {Object} - returns normalized product if product is a set/bundle, otherwise returns original product
 */
export const normalizeSetBundleProduct = (product) => {
    if (!product?.type.set && !product?.type.bundle) return product
    return {
        ...product,
        childProducts: product?.type.set
            ? product.setProducts.map((child) => {
                  return {product: child, quantity: null}
              })
            : product.bundledProducts
    }
}

/**
 * Creates an array of product items used to update the child variant selections for a product bundle,
 * usually in an updateItemToBasket/updateItemsToBasket shopper baskets API call
 *
 * @param {Object} bundle - A product that is a bundle.
 * @param {Array} childProductSelections - An array of the bundle child products with a selected variant.
 *      Each item in the array should have the product, variant, and quantity.
 * @returns {Array} - an array of bundle child product items that have the updated variant
 */
export const getUpdateBundleChildArray = (bundle, childProductSelections) => {
    const itemsToBeUpdated = []
    bundle?.bundledProductItems?.forEach((bundleChild) => {
        const childSelection = childProductSelections?.find(
            (childProduct) =>
                childProduct.product?.id === bundleChild.productId ||
                childProduct.product?.itemId === bundleChild.itemId
        )

        // only update the item if the selected variant is different then what's in the current bundle
        if (childSelection && childSelection.variant?.productId !== bundleChild.productId) {
            itemsToBeUpdated.push({
                itemId: bundleChild.itemId,
                productId: childSelection.variant.productId,
                quantity: childSelection.quantity ?? bundleChild.quantity
            })
        }
    })
    return itemsToBeUpdated
}

/**
 * Used to return the preferred variant if orderable or first orderable or if there is no orderable variants - first variant
 *
 * @param {Object} variants - The products variants.
 * @param {Boolean} c_selectMostSoldVariant - The products c_selectMostSoldVariant.
 * @param {String} c_preferedVariantId - The products c_preferedVariantId.
 * @returns {Object} - The preselected variant.
 */
export const getPreferedVariant = ({variants, c_preferedVariantId}) => {
    // If there is a preferred variant and it is orderable, return it
    if (c_preferedVariantId) {
        const preferedVariant = variants?.find(
            (variant) => variant.productId === c_preferedVariantId
        )
        if (preferedVariant?.orderable) {
            return preferedVariant
        }
    }

    // If there is an orderable variant return the first one
    const firstOrderableVariant = variants?.find((variant) => variant.orderable === true)
    if (firstOrderableVariant) {
        return firstOrderableVariant
    }

    // Return the first variant
    return variants?.[0]
}
/**
 * Returns the master product id of the given variant product if available
 * @param {object} product
 * @return {string|null} master product id or null
 */
export const getMasterProductId = (product) =>
    product?.type?.variant ? product.master?.masterId : null

/**
 * Returns the product ids used to search for BazaarVoice reviews
 * @param {object} product
 * @return {string|null} product ids used to search for BazaarVoice reviews
 */
export const getBVReviewProductIds = (product) =>
    getMasterProductId(product) || product?.id || product?.c_master || product?.productId
/**
 * Returns the basket items for the given product and engrave text
 * @param {object} product
 * @param {string} [engraveText]
 * @param {number} [productQuantity]
 * @returns {[{quantity: number, productId: string, c_engraving?: string}]}
 */
export const getProductAddToBasketItems = (product, engraveText, productQuantity) => {
    let engraved = !engraveText
    return getProductsToAddToCart(product, productQuantity).map((productSetItem) => {
        const productId = productSetItem?.productId || productSetItem?.id
        const quantity = productSetItem?.quantity || productSetItem?.minOrderQuantity || 1

        if (
            !engraved &&
            // extensions and accessories have the engraving flag removed as they cannot be engraved
            isProductEngravable(productSetItem)
        ) {
            engraved = true
            return {
                productId,
                quantity,
                c_engraving: engraveText
            }
        } else {
            return {
                productId,
                quantity
            }
        }
    })
}

/**
 * Get the product display and discount prices
 *
 * @param {Object} product - The product to get the prices for.
 * @param {Boolean} isBonusProduct - True if the product is a bonus product (e.g. price 0), false otherwise.
 * @param {boolean} isCustomNetPriceEnabled
 * @return {{isFree: boolean, displayPrice: number, price: number, hasDiscount: boolean, promotionalPrice: (number|undefined)}}
 */

export const getDisplayPrice = (product, isBonusProduct, isCustomNetPriceEnabled) => {
    let price = Math.max(product?.price, 0) || 0
    const promotion = product?.productPromotions
        ?.filter((promotion) => promotion.promotionalPrice != null)
        .reduce(
            (prev, current) =>
                prev && prev.promotionalPrice < current.promotionalPrice ? prev : current,
            null
        )
    let promotionalPrice = isBonusProduct
        ? 0
        : promotion?.promotionalPrice != null
        ? Math.max(promotion?.promotionalPrice, 0)
        : null

    let displayPrice = promotionalPrice != null ? Math.min(price, promotionalPrice) : price

    if (isCustomNetPriceEnabled) {
        price = calculateNetPrice(price, product?.c_taxRate)
        promotionalPrice = calculateNetPrice(promotionalPrice, product?.c_taxRate)
        displayPrice = calculateNetPrice(displayPrice, product.c_taxRate)
    }

    const hasDiscount = displayPrice !== price

    return {
        price,
        promotionalPrice: hasDiscount ? promotionalPrice : undefined,
        displayPrice,
        hasDiscount,
        isFree: displayPrice === 0
    }
}
/**
 * True if the given product can be engraved, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductEngravable = (product) =>
    Boolean(
        isProductSet(product)
            ? product.setProducts?.some((setPr) => isProductEngravable(setPr))
            : isProductOrderable(product) && product?.c_isEngravable
    )
/**
 * True if the given product is a master product, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductMaster = (product) => Boolean(product?.type?.master)
/**
 * True if the given product is a variant product, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductVariant = (product) => Boolean(product?.type?.variant)
/**
 * True if the given product can be ordered, false otherwise.
 * No need of checking inventory.orderable, instead check c_isOrderable.
 * c_isOrderable is computed on SFCC side and also check available-on-web & available-for-purchase.
 * @param {object} product
 * @returns {boolean}
 */
export const isProductOrderable = (product) =>
    Boolean(
        isProductSet(product)
            ? product.setProducts?.some((p) => isProductOrderable(p))
            : product?.c_isOrderable ?? product?.orderable
    )

/**
 * True if the given product is a product set, false otherwise
 * @param {object} product
 * @returns {boolean}
 */
export const isProductSet = (product) =>
    Boolean(product?.type?.set) || Boolean(product?.productType?.set)

/**
 * True if the given product is a customizable product set, false otherwise
 * @param {object} product
 * @return {boolean}
 */
export const isCustomizableProductSet = (product) =>
    isProductSet(product) && product?.c_displayTemplate === CUSTOMIZABLE_SET_DISPLAY_TEMPLATE

/**
 * Return product quantities
 * @param {Object} product - The product to get the quantities for.
 * @param {number} [maxQuantity] - The maximum quantity of the product that can be added to the basket.
 * @return {{
 *   stockLevel: number,
 *   stepQuantity: number,
 *   minOrderQuantity: number,
 *   maxOrderQuantity: number,
 *   initialQuantity: number,
 * }} - The product quantities.
 */
export const getProductQuantities = (product, maxQuantity) => {
    // ATS includes allocation + preorderBackorderAllocation
    const stockLevel = (() => {
        if (isProductSet(product)) {
            // without set products there's no quantity to compute
            if (!product.setProducts?.length) {
                return 0
            }
            // find the minimum ats that is > 0
            const minAvailableQuantity = product.setProducts.reduce(
                (min, setProduct) =>
                    setProduct?.inventory?.ats ? Math.min(min, setProduct.inventory.ats) : min,
                Number.MAX_SAFE_INTEGER
            )
            // if not ats was found larger than 0, return 0
            if (minAvailableQuantity === Number.MAX_SAFE_INTEGER) {
                return 0
            }
            return minAvailableQuantity
        }
        return product?.inventory?.ats || 0
    })()
    const stepQuantity = product?.stepQuantity || 1
    const minOrderQuantity = stockLevel > 0 ? product?.minOrderQuantity || 1 : 0
    const maxOrderQuantity = maxQuantity != null ? Math.min(stockLevel, maxQuantity) : stockLevel
    const initialQuantity = product?.quantity || product?.minOrderQuantity || 1

    return {
        stockLevel,
        stepQuantity,
        minOrderQuantity,
        maxOrderQuantity,
        initialQuantity
    }
}

/**
 * Return the products that will be added to the basket
 * @param {Object} product - The product to get the products for.
 * @param {number} quantity - The quantity of the product to add to the basket.
 * @return {Array<Object>} - The products that will be added to the basket.
 */
export const getProductsToAddToCart = (product, quantity) => {
    if (!isProductSet(product)) {
        return isProductOrderable(product)
            ? [
                  {
                      ...product,
                      // add the quantity if it is set
                      ...(quantity != null ? {quantity} : {})
                  }
              ]
            : []
    }

    const products = [
        ...product.setProducts.flatMap((setProduct) =>
            getProductsToAddToCart(setProduct, quantity)
        ),
        ...[...(product.setExtensions || []), ...(product.setAccessories || [])]
            .filter((setProduct) => setProduct.isSetProductSelected)
            .flatMap((setProduct) =>
                getProductsToAddToCart(
                    {
                        ...setProduct,
                        // extensions and accessories can't be engraved
                        c_isEngravable: false
                    },
                    // accessories and extensions are added up to their stock level
                    Math.min(
                        quantity || Number.MAX_SAFE_INTEGER,
                        getProductQuantities(setProduct).stockLevel
                    )
                )
            )
    ]

    return products
}

/**
 * Filter product's variation attribute values based on the filter function
 * @param {Object} variationAttributes - The products variation attributes.
 * @param {function (variationValue: Object, variationAttribute: Object): boolean} filter - The filter function to apply to the variation values.
 * @return {Object} - The filtered variation attributes.
 */
export const filterVariationAttributeValues = (variationAttributes, filter) =>
    variationAttributes.map((vars) => ({
        ...vars,
        values: vars.values?.filter((va) => filter(va, vars))
    }))

/**
 * Returns a filter function that only returns the selected variation values
 * @param {Object} variationValues - The selected variation values.
 * @return {function(Object, Object): boolean} - The filter function.
 */
export const onlySelectedVariationValues = (variationValues) => (va, vars) =>
    va.value === variationValues[vars.id]

/**
 * Find the main set product from the set products of a product set.
 * The main product is identified by c_configuratorConfigItemId or c_colorProduct,
 * which points to a material variant. Material variants are master products
 * that are specified as variants to each other via c_variationValuesProductSelector.
 *
 * @param {object} product - the product set to search
 * @returns {object|null} The main set product if found, null otherwise
 */
export const findMainSetProduct = (product) => {
    if (
        !isProductSet(product) ||
        !(product?.c_configuratorConfigItemId || product?.c_colorProduct)
    ) {
        return null
    }
    const mainProductId = product.c_colorProduct || product.c_configuratorConfigItemId

    return product.setProducts?.find(
        (setProduct) =>
            // either the set product ID matches
            setProduct.id === mainProductId ||
            // or its master product ID matches
            getMasterProductId(setProduct) === mainProductId ||
            // or one of its material variant IDs matches
            setProduct.c_variationValuesProductSelector?.some((v) => v.ID === mainProductId)
    )
}

/**
 * Checks if a variant matches the provided variation values - the match requires either the variant to have the same value for a variation key or no value at all
 * @param {object} variant
 * @param {object} variationValues
 * @returns {boolean}
 */
export const variantMatchesValues = (variant, variationValues) => {
    const variationValuesKeys = Object.keys(variationValues || {})
    return variationValuesKeys.every(
        (key) =>
            // either need the same value
            variant.variationValues[key] === variationValues[key] ||
            // or to not have that variation key
            variant.variationValues[key] == null
    )
}

/**
 * Generate a unique ID for a set product in a product set
 * For virtual product sets (single set product), returns null to use PDP query params logic
 * @param {object} sp - the set product
 * @param {number} idx - the index of the set product in the list
 * @param {Array} list - the list of all set products
 * @returns {string|null} The generated ID in format "{productId}_{index}" or null for single-item sets
 */
export const generateSetProductId = (sp, idx, list) =>
    // handle virtual product sets (those that have single set product)
    // by using the pdp logic for query params
    list.length > 1 ? `${sp.id}_${idx}` : null

/**
 * Generate a unique ID for a set accessory
 * @param {object} sp - the accessory product
 * @param {number} idx - the index of the accessory
 * @returns {string} The generated ID in format "{productId}_a{index}"
 */
export const generateSetAccessoryId = (sp, idx) => `${sp.id}_a${idx}`

/**
 * Generate a unique ID for a set extension
 * @param {object} sp - the extension product
 * @param {number} idx - the index of the extension
 * @returns {string} The generated ID in format "{productId}_e{index}"
 */
export const generateSetExtensionId = (sp, idx) => `${sp.id}_e${idx}`
